import { useEffect, useState } from "react";
import { LAST_UPDATED, legalSectionsFor } from "@/pages/legalContent";
import { useT } from "@/i18n/LocaleContext";
import CinematicHero from "@/components/CinematicHero";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_SPLIT_RE = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

const DdValue = ({ value }) => {
  if (typeof value === "string" && EMAIL_RE.test(value.trim())) {
    return (
      <a
        href={`mailto:${value.trim()}`}
        data-testid={`legal-mailto-${value.trim().replace(/[@.]/g, "-")}`}
        className="font-sans text-[14.5px] font-normal text-navy hover:text-gold transition-colors duration-500"
      >
        {value}
      </a>
    );
  }
  return value;
};

/* Splits a paragraph string on email tokens and renders each email as a
   mailto anchor using the same restrained styling used on the Contact /
   Header / Footer, so any legal copy referencing office@qcmlux.com is
   automatically linkified without needing per-block markup. */
const ParagraphWithEmails = ({ text }) => {
  const parts = text.split(EMAIL_SPLIT_RE);
  return parts.map((part, i) => {
    if (EMAIL_RE.test(part)) {
      return (
        <a
          key={i}
          href={`mailto:${part}`}
          data-testid={`legal-mailto-${part.replace(/[@.]/g, "-")}`}
          className="text-navy underline decoration-gold/70 underline-offset-4 hover:text-gold transition-colors duration-500"
        >
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const Block = ({ block }) => {
  if (block.type === "p") return (
    <p className="font-sans text-[15.5px] font-normal leading-[1.75] text-navy">
      <ParagraphWithEmails text={block.text} />
    </p>
  );
  if (block.type === "small_italic") return <p className="font-serif italic text-[16px] md:text-[17px] leading-relaxed text-navy/75 -mt-2">{block.text}</p>;
  if (block.type === "h") return <h3 className="mt-10 mb-4 font-sans text-[10.5px] font-medium uppercase text-mute" style={{ letterSpacing: "0.28em" }}>{block.text}</h3>;
  if (block.type === "ul") return (
    <ul className="list-none space-y-2 pl-0">
      {block.items.map((it, i) => (
        <li key={i} className="relative pl-5 font-sans text-[15px] font-normal leading-[1.75] text-navy before:content-['—'] before:absolute before:left-0 before:text-gold">
          <ParagraphWithEmails text={it} />
        </li>
      ))}
    </ul>
  );
  if (block.type === "dl") return (
    <dl className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-x-6 gap-y-2 border-t border-rule pt-5 mt-2">
      {block.rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="font-sans text-[11px] font-medium uppercase text-mute pt-1" style={{ letterSpacing: "0.16em" }}>{k}</dt>
          <dd className="font-sans text-[14.5px] font-normal text-navy leading-relaxed pb-2 sm:pb-0 border-b sm:border-b-0 border-rule/60">
            <DdValue value={v} />
          </dd>
        </div>
      ))}
    </dl>
  );
  return null;
};

const TocList = ({ sections, activeId }) => (
  <ol className="space-y-3 pl-0" data-testid="legal-toc-list">
    {sections.map((s, i) => {
      const active = activeId === s.id;
      return (
        <li key={s.id} className="flex items-start gap-3">
          <span className={["font-sans text-[10.5px] font-medium pt-[3px] w-6 shrink-0", active ? "text-gold" : "text-mute"].join(" ")} style={{ letterSpacing: "0.16em" }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <a href={`#${s.id}`} data-testid={`legal-toc-${s.id}`} className={["font-sans text-[13.5px] font-normal leading-snug transition-colors duration-500", active ? "text-navy" : "text-mute hover:text-navy"].join(" ")}>
            {s.title}
          </a>
        </li>
      );
    })}
  </ol>
);

export const Legal = () => {
  const { locale, t } = useT();
  const sections = legalSectionsFor(locale);
  const [activeId, setActiveId] = useState(sections[0].id);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!("IntersectionObserver" in window) || els.length === 0) return;
    const io = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveId(visible[0].target.id);
    }, { rootMargin: "-30% 0px -60% 0px", threshold: 0 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [sections]);

  return (
    <>
      <CinematicHero
        imageSrc="/images/legal-hero.jpg"
        imageAlt={t("hero_cinematic.legal.alt")}
        eyebrow={t("hero_cinematic.legal.eyebrow")}
        title={t("hero_cinematic.legal.title")}
        subtitle={t("hero_cinematic.legal.subtitle")}
        testIdPrefix="legal-hero"
      />

      <section id="content" data-testid="legal-page" className="bg-cream">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 pt-20 md:pt-28 pb-24 md:pb-40">
          {/* Slim type row — Last updated only; hero already carries the title */}
          <div className="max-w-3xl">
            <p data-testid="legal-last-updated" className="reveal font-sans text-[12.5px] font-medium uppercase text-mute" style={{ letterSpacing: "0.22em" }}>
              {t("legal.last_updated_label")}: {LAST_UPDATED}
            </p>
          </div>

          {/* Mobile TOC accordion */}
          <div className="md:hidden mt-14 border-y border-rule">
            <button
              type="button"
              data-testid="legal-toc-mobile-toggle"
              aria-expanded={tocOpen}
              onClick={() => setTocOpen((v) => !v)}
              className="w-full flex items-center justify-between py-4 font-sans text-[11px] font-medium uppercase text-navy"
              style={{ letterSpacing: "0.24em" }}
            >
              <span>{t("legal.toc_mobile_label")}</span>
              <span className="font-serif text-gold text-lg" aria-hidden>{tocOpen ? "−" : "+"}</span>
            </button>
            {tocOpen && (
              <div className="pb-6" data-testid="legal-toc-mobile">
                <TocList sections={sections} activeId={activeId} />
              </div>
            )}
          </div>

          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
            <aside data-testid="legal-toc-desktop" className="hidden md:block md:col-span-3">
              <div className="sticky top-24">
                <div className="font-sans text-[10.5px] font-medium uppercase text-mute mb-5" style={{ letterSpacing: "0.24em" }}>
                  {t("legal.toc_label")}
                </div>
                <TocList sections={sections} activeId={activeId} />
              </div>
            </aside>

            <div className="md:col-span-8 md:col-start-5 max-w-[720px]">
              {sections.map((s, i) => (
                <article
                  key={s.id}
                  id={s.id}
                  data-testid={`legal-section-${s.id}`}
                  className={["scroll-mt-32 reveal", i > 0 ? "pt-14 md:pt-20 mt-14 md:mt-20 border-t border-rule/70" : ""].join(" ")}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="h-px w-8 bg-gold" aria-hidden />
                    <span className="font-sans text-[10.5px] font-medium uppercase text-mute" style={{ letterSpacing: "0.28em" }}>{s.number}</span>
                  </div>
                  <h2 className="font-serif font-normal text-navy text-[30px] md:text-[38px] leading-[1.1] mb-8">
                    {s.title}
                  </h2>
                  <div className="space-y-5">{s.blocks.map((b, k) => <Block key={k} block={b} />)}</div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
      <div className="section-bridge-cream-navy" aria-hidden data-testid="legal-bridge-footer" />
    </>
  );
};

export default Legal;
